"use client"

import { useTranslations } from "next-intl"
import { cn } from "@/lib/utils"
import {
  Award, Calendar, ExternalLink, Fingerprint, Hash, Languages,
  Layers, Package, Shield, Sparkles, Target, TriangleAlert,
} from "lucide-react"

export interface TcgCardDetailsData {
  listing_id: string
  card_name: string
  card_number?: string | null
  print_year?: number | null
  print_run_number?: number | null
  print_run_total?: number | null
  tcg_name?: string | null
  tcg_slug?: string | null
  set_name?: string | null
  rarity_name?: string | null
  language_name?: string | null
  format_name?: string | null
  condition_name?: string | null
  grading_company_name?: string | null
  grade_label?: string | null
  grade_value?: number | null
  cert_number?: string | null
  cert_verify_url?: string | null
  previous_sale_url?: string | null
  bgs_centering?: number | null
  bgs_corners?: number | null
  bgs_edges?: number | null
  bgs_surface?: number | null
  left_pct?: number | null
  right_pct?: number | null
  top_pct?: number | null
  bottom_pct?: number | null
  is_first_edition?: boolean
  is_shadowless?: boolean
  is_reverse_holo?: boolean
  is_stamped_promo?: boolean
  is_misprint?: boolean
  is_alt_art?: boolean
  is_autograph?: boolean
  is_rookie?: boolean
  is_foil_variant?: boolean
  is_signed?: boolean
  is_patch_relic?: boolean
  is_foreign_black_border?: boolean
  mtg_print_variant?: string | null
  sport?: string | null
  player_name?: string | null
  provenance_tags?: string[] | null
  provenance_notes?: string | null
  lot_card_count?: number | null
  lot_commons_count?: number | null
  lot_uncommons_count?: number | null
  lot_rares_plus_count?: number | null
  defects?: string[]
}

function gradeColor(value: number | null | undefined): string {
  if (value == null) return "bg-muted text-foreground"
  if (value >= 9) return "bg-emerald-500 text-white"
  if (value >= 7) return "bg-amber-500 text-white"
  return "bg-orange-500 text-white"
}

function bgsSubColor(value: number): string {
  if (value >= 9.5) return "text-emerald-500"
  if (value >= 9) return "text-emerald-400"
  if (value >= 8) return "text-amber-400"
  return "text-orange-400"
}

const MTG_VARIANT_LABELS: Record<string, string> = {
  alpha: "Alpha", beta: "Beta", unlimited: "Unlimited", revised: "Revised",
  fbb: "FBB", fwb: "FWB", summer: "Summer", ce: "CE", ice_age: "Ice Age",
}

function GradingBadge({ company, label, value }: { company: string; label?: string | null; value?: number | null }) {
  return (
    <div className={cn("inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-semibold", gradeColor(value))}>
      <Shield className="h-3.5 w-3.5" />
      <span>{company} {value}</span>
      {label ? <span className="font-normal opacity-80">· {label}</span> : null}
    </div>
  )
}

type SpecTagKey =
  | "firstEdition" | "shadowless" | "reverseHolo" | "stampedPromo" | "misprint"
  | "altArt" | "foil" | "autograph" | "signed" | "patchRelic" | "rookie" | "foreignBlackBorder"

function collectSpecTagKeys(card: TcgCardDetailsData): SpecTagKey[] {
  const keys: SpecTagKey[] = []
  if (card.is_first_edition) keys.push("firstEdition")
  if (card.is_shadowless) keys.push("shadowless")
  if (card.is_reverse_holo) keys.push("reverseHolo")
  if (card.is_stamped_promo) keys.push("stampedPromo")
  if (card.is_misprint) keys.push("misprint")
  if (card.is_alt_art) keys.push("altArt")
  if (card.is_foil_variant) keys.push("foil")
  if (card.is_autograph) keys.push("autograph")
  if (card.is_signed) keys.push("signed")
  if (card.is_patch_relic) keys.push("patchRelic")
  if (card.is_rookie) keys.push("rookie")
  if (card.is_foreign_black_border) keys.push("foreignBlackBorder")
  return keys
}

type DetailKey = "tcg" | "set" | "rarity" | "cardNumber" | "language" | "format" | "year" | "printRun" | "player" | "sport"

function collectDetailEntries(card: TcgCardDetailsData): { key: DetailKey; icon: typeof Award; value: string }[] {
  const entries: { key: DetailKey; icon: typeof Award; value: string }[] = []
  if (card.tcg_name) entries.push({ key: "tcg", icon: Layers, value: card.tcg_name })
  if (card.set_name) entries.push({ key: "set", icon: Layers, value: card.set_name })
  if (card.rarity_name) entries.push({ key: "rarity", icon: Sparkles, value: card.rarity_name })
  if (card.card_number) entries.push({ key: "cardNumber", icon: Hash, value: card.card_number })
  if (card.language_name) entries.push({ key: "language", icon: Languages, value: card.language_name })
  if (card.format_name) entries.push({ key: "format", icon: Package, value: card.format_name })
  if (card.print_year) entries.push({ key: "year", icon: Calendar, value: String(card.print_year) })
  if (card.print_run_number || card.print_run_total) {
    const run = card.print_run_number && card.print_run_total
      ? `#${card.print_run_number} / ${card.print_run_total}`
      : card.print_run_total ? `/ ${card.print_run_total}` : `#${card.print_run_number}`
    entries.push({ key: "printRun", icon: Fingerprint, value: run })
  }
  if (card.player_name) entries.push({ key: "player", icon: Award, value: card.player_name })
  if (card.sport) entries.push({ key: "sport", icon: Award, value: card.sport })
  return entries
}

function hasBgsSubgrades(card: TcgCardDetailsData): boolean {
  return card.bgs_centering != null && card.bgs_corners != null && card.bgs_edges != null && card.bgs_surface != null
}

function hasNonDefaultCentering(card: TcgCardDetailsData): boolean {
  return (card.left_pct != null && card.left_pct !== 50) || (card.top_pct != null && card.top_pct !== 50)
}

function hasLotBreakdown(card: TcgCardDetailsData): boolean {
  return (card.lot_card_count ?? 0) > 0
}

export function TcgCardDetails({
  card,
  className,
}: {
  card: TcgCardDetailsData
  className?: string
}) {
  const t = useTranslations("listing.tcgDetails")
  const isGraded = Boolean(card.grading_company_name && card.grade_value != null)
  const specTagKeys = collectSpecTagKeys(card)
  const mtgVariant = card.mtg_print_variant ? (MTG_VARIANT_LABELS[card.mtg_print_variant] ?? card.mtg_print_variant) : null
  const details = collectDetailEntries(card)
  const showBgs = isGraded && hasBgsSubgrades(card)
  const showCentering = !isGraded && hasNonDefaultCentering(card)
  const showLot = hasLotBreakdown(card)
  const provenanceTags = (card.provenance_tags ?? []).filter(Boolean)
  const defects = (card.defects ?? []).filter(Boolean)

  return (
    <div className={cn("space-y-3", className)}>
      <div className="flex items-center flex-wrap gap-2">
        {isGraded ? (
          <GradingBadge company={card.grading_company_name!} label={card.grade_label} value={card.grade_value} />
        ) : card.condition_name ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1.5 text-sm font-medium text-foreground dark:bg-white/20 dark:text-white">
            {card.condition_name}
          </span>
        ) : null}
        {isGraded && card.cert_number ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground dark:bg-white/15 dark:text-white/70">
            {t("certNumber", { number: card.cert_number })}
          </span>
        ) : null}
        {card.cert_verify_url ? (
          <a
            href={card.cert_verify_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium bg-muted text-foreground hover:bg-accent transition-colors dark:bg-white/10 dark:text-white dark:hover:bg-white/20"
          >
            <ExternalLink className="h-3 w-3" />
            {t("viewCertification")}
          </a>
        ) : null}
        {card.previous_sale_url ? (
          <a
            href={card.previous_sale_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium bg-muted text-foreground hover:bg-accent transition-colors dark:bg-white/10 dark:text-white dark:hover:bg-white/20"
          >
            <ExternalLink className="h-3 w-3" />
            {t("previousSale")}
          </a>
        ) : null}
      </div>

      {showBgs ? (
        <div className="flex items-center gap-3 rounded-xl bg-muted/50 dark:bg-white/5 px-3 py-2 text-xs">
          {([
            ["Centering", card.bgs_centering!],
            ["Corners", card.bgs_corners!],
            ["Edges", card.bgs_edges!],
            ["Surface", card.bgs_surface!],
          ] as const).map(([label, value]) => (
            <div key={label} className="flex flex-col items-center gap-0.5">
              <span className={cn("text-sm font-bold tabular-nums", bgsSubColor(value))}>{value}</span>
              <span className="text-muted-foreground dark:text-white/40">{label}</span>
            </div>
          ))}
        </div>
      ) : null}

      {(specTagKeys.length > 0 || mtgVariant) ? (
        <div className="flex items-center flex-wrap gap-1.5">
          {specTagKeys.map((key) => (
            <span key={key} className="inline-flex items-center gap-1 rounded-full bg-violet-500/15 px-2.5 py-1 text-xs font-medium text-violet-600 dark:text-violet-400 border border-violet-500/20">
              <Sparkles className="h-3 w-3" />
              {t(key)}
            </span>
          ))}
          {mtgVariant ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-violet-500/15 px-2.5 py-1 text-xs font-medium text-violet-600 dark:text-violet-400 border border-violet-500/20">
              <Sparkles className="h-3 w-3" />
              {mtgVariant}
            </span>
          ) : null}
        </div>
      ) : null}

      {details.length > 0 ? (
        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          {details.map((d) => (
            <div key={d.key} className="flex items-center gap-2 min-w-0">
              <d.icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <span className="text-muted-foreground shrink-0">{t(d.key)}</span>
              <span className="font-medium truncate text-foreground">{d.value}</span>
            </div>
          ))}
        </div>
      ) : null}

      {showCentering ? (
        <div className="flex items-center gap-2 text-sm">
          <Target className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <span className="text-muted-foreground">{t("centering")}</span>
          <span className="font-medium text-foreground">{card.left_pct}/{card.right_pct} · {card.top_pct}/{card.bottom_pct}</span>
        </div>
      ) : null}

      {defects.length > 0 ? (
        <div className="flex items-center flex-wrap gap-1.5">
          {defects.map((d) => (
            <span key={d} className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2.5 py-1 text-xs font-medium text-amber-600 dark:text-amber-400 border border-amber-500/20">
              <TriangleAlert className="h-3 w-3" />
              {t(`defects.${d}`)}
            </span>
          ))}
        </div>
      ) : null}

      {showLot ? (
        <div className="flex items-center gap-3 text-sm">
          <Package className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <span className="text-muted-foreground">{t("lot")}</span>
          <span className="font-medium text-foreground">
            {t("lotCards", { count: card.lot_card_count ?? 0 })}
            {card.lot_rares_plus_count ? ` · ${t("lotRares", { count: card.lot_rares_plus_count })}` : ""}
          </span>
        </div>
      ) : null}

      {provenanceTags.length > 0 ? (
        <div className="flex items-center flex-wrap gap-1.5">
          {provenanceTags.map((tag) => (
            <span key={tag} className="inline-flex items-center rounded-full bg-muted/60 dark:bg-white/10 px-2.5 py-1 text-xs font-medium text-muted-foreground dark:text-white/60">
              {t(`provenance.${tag}`)}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  )
}

export function TcgCardDetailsDark({
  card,
  className,
}: {
  card: TcgCardDetailsData
  className?: string
}) {
  const t = useTranslations("listing.tcgDetails")
  const isGraded = Boolean(card.grading_company_name && card.grade_value != null)
  const specTagKeys = collectSpecTagKeys(card)
  const mtgVariant = card.mtg_print_variant ? (MTG_VARIANT_LABELS[card.mtg_print_variant] ?? card.mtg_print_variant) : null
  const details = collectDetailEntries(card)
  const showBgs = isGraded && hasBgsSubgrades(card)
  const showCentering = !isGraded && hasNonDefaultCentering(card)
  const showLot = hasLotBreakdown(card)
  const provenanceTags = (card.provenance_tags ?? []).filter(Boolean)
  const defects = (card.defects ?? []).filter(Boolean)

  return (
    <div className={cn("space-y-3", className)}>
      <div className="flex items-center flex-wrap gap-2">
        {isGraded ? (
          <GradingBadge company={card.grading_company_name!} label={card.grade_label} value={card.grade_value} />
        ) : card.condition_name ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 text-sm font-medium text-white">
            {card.condition_name}
          </span>
        ) : null}
        {isGraded && card.cert_number ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-1 text-xs font-medium text-white/70">
            {t("certNumber", { number: card.cert_number })}
          </span>
        ) : null}
        {card.cert_verify_url ? (
          <a
            href={card.cert_verify_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium bg-white/10 text-white hover:bg-white/20 transition-colors"
          >
            <ExternalLink className="h-3 w-3" />
            {t("viewCertification")}
          </a>
        ) : null}
        {card.previous_sale_url ? (
          <a
            href={card.previous_sale_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium bg-white/10 text-white hover:bg-white/20 transition-colors"
          >
            <ExternalLink className="h-3 w-3" />
            {t("previousSale")}
          </a>
        ) : null}
      </div>

      {showBgs ? (
        <div className="flex items-center gap-3 rounded-xl bg-white/5 px-3 py-2 text-xs">
          {([
            ["Centering", card.bgs_centering!],
            ["Corners", card.bgs_corners!],
            ["Edges", card.bgs_edges!],
            ["Surface", card.bgs_surface!],
          ] as const).map(([label, value]) => (
            <div key={label} className="flex flex-col items-center gap-0.5">
              <span className={cn("text-sm font-bold tabular-nums", bgsSubColor(value))}>{value}</span>
              <span className="text-white/40">{label}</span>
            </div>
          ))}
        </div>
      ) : null}

      {(specTagKeys.length > 0 || mtgVariant) ? (
        <div className="flex items-center flex-wrap gap-1.5">
          {specTagKeys.map((key) => (
            <span key={key} className="inline-flex items-center gap-1 rounded-full bg-violet-500/20 px-2.5 py-1 text-xs font-medium text-violet-400 border border-violet-500/25">
              <Sparkles className="h-3 w-3" />
              {t(key)}
            </span>
          ))}
          {mtgVariant ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-violet-500/20 px-2.5 py-1 text-xs font-medium text-violet-400 border border-violet-500/25">
              <Sparkles className="h-3 w-3" />
              {mtgVariant}
            </span>
          ) : null}
        </div>
      ) : null}

      {details.length > 0 ? (
        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          {details.map((d) => (
            <div key={d.key} className="flex items-center gap-2 min-w-0">
              <span className="text-white/50 shrink-0">{t(d.key)}</span>
              <span className="font-medium truncate text-white">{d.value}</span>
            </div>
          ))}
        </div>
      ) : null}

      {showCentering ? (
        <div className="flex items-center gap-2 text-sm">
          <span className="text-white/50">{t("centering")}</span>
          <span className="font-medium text-white">{card.left_pct}/{card.right_pct} · {card.top_pct}/{card.bottom_pct}</span>
        </div>
      ) : null}

      {defects.length > 0 ? (
        <div className="flex items-center flex-wrap gap-1.5">
          {defects.map((d) => (
            <span key={d} className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-2.5 py-1 text-xs font-medium text-amber-400 border border-amber-500/25">
              <TriangleAlert className="h-3 w-3" />
              {t(`defects.${d}`)}
            </span>
          ))}
        </div>
      ) : null}

      {showLot ? (
        <div className="flex items-center gap-2 text-sm">
          <span className="text-white/50">{t("lot")}</span>
          <span className="font-medium text-white">
            {t("lotCards", { count: card.lot_card_count ?? 0 })}
            {card.lot_rares_plus_count ? ` · ${t("lotRares", { count: card.lot_rares_plus_count })}` : ""}
          </span>
        </div>
      ) : null}

      {provenanceTags.length > 0 ? (
        <div className="flex items-center flex-wrap gap-1.5">
          {provenanceTags.map((tag) => (
            <span key={tag} className="inline-flex items-center rounded-full bg-white/10 px-2.5 py-1 text-xs font-medium text-white/60">
              {t(`provenance.${tag}`)}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  )
}
